
import { defineComponent, ref, onMounted, computed } from "vue";
import { usePlugin } from "@/plugins";
import {
  PositionDocument,
  GetPositionDocumentResponse,
  GetPositionDocumentRequest,
  positionDocumentTypes,
} from "@/types";
import BackButton from "@/components/BackButton.vue";
import StatusBadge from "@/components/StatusBadge.vue";
import InputUser from "@/components/InputUser.vue";
import ReleaseStatusBadge from "@/components/ReleaseStatusBadge.vue";
import { FilterMatchMode, FilterOperator } from "primevue/api";

export default defineComponent({
  name: "PositionDocumentList",
  components: { BackButton, StatusBadge, ReleaseStatusBadge, InputUser },
  setup() {
    const {
      setLoading,
      formatAppDate,
      onRouterPush,
      error,
      axios,
      success,
      dayjs,
      confirmation,
    } = usePlugin();

    const filters = ref({
      id: { value: null, matchMode: FilterMatchMode.CONTAINS },
      "employee.id": {
        value: null,
        matchMode: FilterMatchMode.EQUALS,
      },
      "employee.divisionDescription": {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      },
      "employee.departmentDescription": {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      },
      releaseStatus: {
        value: null,
        matchMode: FilterMatchMode.EQUALS,
      },
      status: {
        value: null,
        matchMode: FilterMatchMode.EQUALS,
      },
    });

    const selectedDate = ref<Date[]>([
      dayjs()
        .startOf("month")
        .toDate(),
      dayjs()
        .endOf("month")
        .toDate(),
    ]);
    const positionDocuments = ref<PositionDocument[]>([]);
    const onEditDocument = (id: string) => {
      onRouterPush({ name: "editPositionDocument", params: { id: id } });
    };
    const onViewDocument = (id: string) => {
      onRouterPush({ name: "positionAssessment", params: { id: id } });
    };
    const onCreatePositionDocument = () => {
      onRouterPush({ name: "createPositionDocument" });
    };

    const onCopyDocument = (id: number) => {
      confirmation.require({
        header: "คัดลอกข้อมูล",
        message: "กรุณายืนยันเพื่อทำการคัดลอกข้อมูลเพื่อสร้างเอกสารใบใหม่?",
        acceptLabel: "คัดลอกข้อมูล",
        accept: () => {
          onRouterPush({
            name: "editPositionDocument",
            params: { id: id, copy: "true" },
          });
        },
        rejectLabel: "ยกเลิก",
      });
    };

    const loadDocument = async () => {
      setLoading(true);
      try {
        const result = await axios.post<GetPositionDocumentResponse>(
          "api/v1/services/positionDocument/get",
          {
            fromDate: selectedDate.value[0],
            toDate: selectedDate.value[1],
          } as GetPositionDocumentRequest
        );

        positionDocuments.value = result.data.positionDocuments ?? [];
        success("ดึงข้อมูลสำเร็จแล้ว");
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };

    const onSearch = () => {
      loadDocument();
    };

    const releases = computed(() => [
      ...new Set(positionDocuments.value.map((x) => x.releaseStatus)),
    ]);
    const statuses = computed(() => [
      ...new Set(positionDocuments.value.map((x) => x.status)),
    ]);

    onMounted(() => loadDocument());

    return {
      positionDocuments,
      formatAppDate,
      onEditDocument,
      onCopyDocument,
      onViewDocument,
      onCreatePositionDocument,
      positionDocumentTypes,
      selectedDate,
      onSearch,
      filters,
      releases,
      statuses,
    };
  },
});
